import { useState } from 'react';
import type { AddressConfig, PostcodeAddress, PostcodeLookupOutcome } from '@jfh/intake-shared';
import { formatUkPostcode, postcodeAddressLabel, postcodeAddressValue } from '@jfh/intake-shared';
import type { AddressValue } from './lib/steps.js';
import type { LookupPostcode } from './lib/postcodeLookup.js';

type Status =
  | { kind: 'idle' }
  | { kind: 'searching' }
  | { kind: 'found'; addresses: PostcodeAddress[] }
  | { kind: 'message'; tone: 'warn' | 'error'; text: string };

function messageFor(r: Exclude<PostcodeLookupOutcome, { kind: 'found' }>): Status {
  switch (r.kind) {
    case 'invalid':
      return { kind: 'message', tone: 'error', text: 'Enter a full UK postcode, for example IP5 1AB.' };
    case 'not_found':
      return { kind: 'message', tone: 'error', text: "We couldn't find any addresses for that postcode. Check it, or enter the address yourself." };
    case 'busy':
      return { kind: 'message', tone: 'warn', text: 'Too many look-ups. Please wait a minute and try again, or enter the address yourself.' };
    case 'unavailable':
      return { kind: 'message', tone: 'warn', text: "We couldn't look addresses up just now. Please enter the address yourself." };
  }
}

/**
 * "Postcode" + Find address → "Choose your address" → the lines fill in
 * (still editable). `onPicked` gets the address's lines; `onManual` switches
 * to typing the address.
 */
export function PostcodeFinder({
  lookup,
  address,
  value,
  onPicked,
  onManual,
}: {
  lookup: LookupPostcode;
  address: AddressConfig | undefined;
  value: AddressValue;
  onPicked: (patch: Partial<AddressValue>) => void;
  onManual: () => void;
}) {
  const [postcode, setPostcode] = useState(value.postcode ?? '');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const find = async () => {
    if (!formatUkPostcode(postcode)) {
      setStatus({ kind: 'message', tone: 'error', text: 'Enter a full UK postcode, for example IP5 1AB.' });
      return;
    }
    setStatus({ kind: 'searching' });
    const r = await lookup(postcode);
    setStatus(r.kind === 'found' ? { kind: 'found', addresses: r.addresses } : messageFor(r));
  };

  return (
    <div className="rdr-postcode-find">
      <div className="rdr-postcode-lookup">
        <input
          className="input rdr-postcode"
          value={postcode}
          placeholder="Postcode"
          aria-label="Postcode"
          autoComplete="postal-code"
          onChange={(e) => setPostcode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void find();
            }
          }}
        />
        <button type="button" className="btn" onClick={() => void find()} disabled={status.kind === 'searching'}>
          {status.kind === 'searching' ? 'Finding…' : 'Find address'}
        </button>
      </div>
      {status.kind === 'found' && (
        <select
          className="select"
          aria-label="Choose your address"
          defaultValue=""
          onChange={(e) => {
            const a = status.addresses[Number(e.target.value)];
            if (a) onPicked(postcodeAddressValue(a, address));
          }}
        >
          <option value="" disabled>
            Choose your address ({status.addresses.length} found)
          </option>
          {status.addresses.map((a, i) => (
            <option key={i} value={i}>
              {postcodeAddressLabel(a)}
            </option>
          ))}
        </select>
      )}
      {status.kind === 'message' && <div className={`rdr-company-status ${status.tone}`}>{status.text}</div>}
      <button type="button" className="rdr-link" onClick={onManual}>
        Enter the address yourself
      </button>
    </div>
  );
}
